/**
 * Owner-bound content (core-content cutover).
 *
 * Protected local content in this browser profile belongs to exactly one
 * account, recorded durably in the `app-meta` IDB store as
 * `{ key: 'content-owner', value: <ownerId> }`. Nothing here infers an
 * owner for existing local work from whoever happens to be signed in.
 *
 * Why fail closed on an absent binding: before this release, local
 * storage was global to the browser profile. Two accounts used on one
 * device shared one IndexedDB. So a row with no recorded owner cannot be
 * attributed to the current session without guessing, and guessing wrong
 * shows one person another person's prayers. The only way content
 * becomes displayable again is a verified owner-bound install
 * (src/lib/content-owner-restore.ts), which the operator runs with
 * counts and key-set digests it can check.
 *
 * A genuinely empty writable profile is different: one atomic transaction
 * proves every protected store empty before binding the authenticated owner,
 * after which normal lifecycle pull restores that account's server data.
 * Missing metadata alone must never strand a new browser on the safety screen.
 * An existing different binding is never replaced, even when its stores appear
 * empty: one browser profile belongs to one account in this release.
 *
 * Account switching remains fail-closed whenever protected local work exists:
 * a session id that differs from that work's binding is `recovery-required`,
 * never a silent re-bind.
 *
 * RELATIVE imports (not `@/`): this module is loaded by
 * scripts/probe-core-readonly.mjs under plain tsx, where the alias does
 * not resolve. Both dependencies are probe-clean leaves.
 */

import {
  bindEmptyContentOwnerRowAtomically,
  getContentOwnerBindingRow,
  inspectUnboundContentOwnerProfileRow,
  setContentOwnerBindingRow,
  isRestoreBypassActive,
} from './db';
import { isCoreReadOnly } from './core-cutover';
import { hasNonEmptyRecoveryText } from './content-escrow/semantic';
import { isOwnerTransitionCoordinationKey } from './owner-transition-keys';

/** The two states the owner gate can resolve to. */
export type OwnerGateState = 'ok' | 'recovery-required';

/** Read the durable binding for this browser profile, or null if unbound. */
export async function getContentOwnerBinding(): Promise<string | null> {
  return getContentOwnerBindingRow();
}

/**
 * Record the durable binding. Callable ONLY from inside a
 * `db.__dangerouslyRunRestoreInstall` callback (Node probe fixtures only).
 * Any other caller throws: binding an owner outside a verified install
 * is exactly the guess this whole module exists to prevent.
 */
export async function bindContentOwner(ownerId: string): Promise<void> {
  if (!isRestoreBypassActive()) {
    throw new Error(
      'bindContentOwner: only callable inside a restore install (owner binding is never inferred)',
    );
  }
  const trimmed = ownerId.trim();
  if (trimmed === '') {
    throw new Error('bindContentOwner: ownerId must be a non-empty string');
  }
  await setContentOwnerBindingRow(trimmed);
}

export type EmptyProfileOwnerBootstrapResult =
  | 'bound'
  | 'already-bound'
  | 'recovery-required';

const OWNER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

// Non-content preferences and monotonically safe bookkeeping may survive on a
// browser that has no protected work. They must not manufacture a recovery
// wall. Unknown `adia-*` keys still fail closed because a future key may carry
// authored content; adding a harmless preference later means reviewing and
// extending this allowlist deliberately.
const EMPTY_PROFILE_LOCAL_STORAGE_ALLOWLIST = new Set([
  'adia-bible-book-context-collapsed',
  'adia-bible-chapter-context-collapsed',
  'adia-bible-theme',
  'adia-bible-verse-coachmark-seen',
  'adia-cloud-account',
  // Retired 'Default new prayers to private' preference (P18). The app no
  // longer reads or writes it, but old browsers may still hold the value;
  // keep it recognized so it never manufactures a recovery wall.
  'adia-default-privacy',
  'adia-lang',
  'adia-last-discovery',
  'adia-last-surface',
  'adia-last-sync',
  'adia-study-mode',
  'adia-text-scale',
  'adia-visited',
  'adia-voice-disclosure-seen',
]);

const OWNER_DERIVED_LOCAL_STORAGE_KEYS = new Set([
  'adia-who-merge',
  'adia-who-dismissed-pairs',
]);

function hasProtectedLocalStorageValue(key: string, value: string): boolean {
  // These two exact keys coordinate the transition that is currently
  // repeating this ownership check. They never carry user content. In
  // particular, a drain-failure value is only an opaque transition UUID and
  // is enforced by runExclusiveOwnerTransition before replacement begins.
  if (isOwnerTransitionCoordinationKey(key)) return false;
  if (key === 'adia-prayer-draft' || key === 'adia-interrupted-interim') {
    return hasNonEmptyRecoveryText({ key, value });
  }
  if (OWNER_DERIVED_LOCAL_STORAGE_KEYS.has(key)) {
    return value.trim().length > 0;
  }
  if (key === 'adia-leaf-glow-seen') {
    if (value.trim().length === 0) return false;
    try {
      const parsed = JSON.parse(value) as unknown;
      return !Array.isArray(parsed) || parsed.length > 0;
    } catch {
      return true;
    }
  }
  return !EMPTY_PROFILE_LOCAL_STORAGE_ALLOWLIST.has(key);
}

/**
 * What an inspection of an unbound profile established. `protected` means
 * owner-bearing content was actually found. `unreadable` means the browser's
 * storage could not be inspected (it would not open, a read failed, or the
 * localStorage pass could not complete consistently): nothing was found and
 * nothing was proven empty. Only `empty` is ever permission to bind.
 */
export type UnboundProfileInspection = 'empty' | 'protected' | 'unreadable';

/** Three-way localStorage half of the empty-profile proof. Never throws. */
export function inspectEmptyProfileLocalStorage(
  storage: Storage | null | undefined,
): UnboundProfileInspection {
  if (!storage) return 'unreadable';
  try {
    const initialLength = storage.length;
    for (let index = 0; index < initialLength; index += 1) {
      const key = storage.key(index);
      if (key === null) return 'unreadable';
      if (!key.startsWith('adia-')) continue;
      const value = storage.getItem(key);
      if (value === null) return 'unreadable';
      if (hasProtectedLocalStorageValue(key, value)) return 'protected';
    }
    return storage.length === initialLength ? 'empty' : 'unreadable';
  } catch {
    return 'unreadable';
  }
}

/** Fail-closed localStorage half of the empty-profile proof. */
export function isEmptyProfileLocalStorageSafe(
  storage: Storage | null | undefined =
    typeof localStorage === 'undefined' ? undefined : localStorage,
): boolean {
  return inspectEmptyProfileLocalStorage(storage) === 'empty';
}

function inspectCurrentLocalStorage(): UnboundProfileInspection {
  let storage: Storage | undefined;
  try {
    storage = typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    // A browser that refuses access to localStorage itself (for example a
    // SecurityError) cannot be inspected.
    return 'unreadable';
  }
  return inspectEmptyProfileLocalStorage(storage);
}

/**
 * Classify an unbound profile without changing it, keeping "could not inspect"
 * apart from "content found" so the owner gate can say which one happened.
 * The localStorage proof is checked on both sides of the IndexedDB
 * transaction so a concurrent draft cannot turn an empty result into
 * permission to present the wrong state. Never throws.
 */
export async function inspectUnboundContentOwnerProfileDetailed(): Promise<
  UnboundProfileInspection
> {
  const before = inspectCurrentLocalStorage();
  if (before !== 'empty') return before;
  let profile: UnboundProfileInspection;
  try {
    profile = await inspectUnboundContentOwnerProfileRow();
  } catch {
    // adia-db would not open, or the inspection could not start.
    return 'unreadable';
  }
  if (profile !== 'empty') return profile;
  return inspectCurrentLocalStorage();
}

/**
 * Two-way form for the exclusive adoption (owner-bootstrap.ts): anything not
 * proven empty, including storage that cannot be inspected, is `protected`.
 */
export async function inspectUnboundContentOwnerProfile(): Promise<
  'empty' | 'protected'
> {
  return (await inspectUnboundContentOwnerProfileDetailed()) === 'empty'
    ? 'empty'
    : 'protected';
}

/**
 * Writable-release-only bootstrap for a truly empty browser profile.
 *
 * The caller supplies a synchronous auth-current guard. db.ts evaluates that
 * guard together with the localStorage proof before and during one transaction
 * that locks every owner-bearing IndexedDB store. Existing content and an
 * existing different binding never flow through this path; they remain behind
 * the account-safety gate.
 */
export async function bootstrapEmptyContentOwner(
  requestedOwnerId: string,
  isAuthenticatedOwnerCurrent: () => boolean,
): Promise<EmptyProfileOwnerBootstrapResult> {
  const ownerId = requestedOwnerId.trim();
  if (
    isCoreReadOnly() ||
    !OWNER_ID_RE.test(ownerId) ||
    !isAuthenticatedOwnerCurrent()
  ) return 'recovery-required';

  const stillAuthorizedAndLocallyEmpty = (): boolean =>
    isAuthenticatedOwnerCurrent() && isEmptyProfileLocalStorageSafe();
  if (!stillAuthorizedAndLocallyEmpty()) return 'recovery-required';

  const result = await bindEmptyContentOwnerRowAtomically(
    ownerId,
    stillAuthorizedAndLocallyEmpty,
  );
  return result === 'unsafe' ? 'recovery-required' : result;
}

/**
 * PURE gate decision. No IDB, no session lookup, no side effects, so the
 * whole matrix is testable.
 *
 *   binding null                    -> 'recovery-required'
 *     (the writable hook may first attempt the separate atomic empty-profile
 *      bootstrap; this pure resolver never guesses that eligibility)
 *     (legacy global storage; untrusted until an owner-bound install)
 *   binding present, ids equal      -> 'ok'
 *   binding present, ids differ     -> 'recovery-required'
 *   binding present, signed out     -> 'recovery-required'
 */
export function resolveOwnerGate(
  sessionUserId: string | null,
  binding: string | null,
): OwnerGateState {
  if (binding === null) return 'recovery-required';
  if (sessionUserId === null) return 'recovery-required';
  return sessionUserId === binding ? 'ok' : 'recovery-required';
}
